import { Injectable } from '@angular/core';
import { DataTaskService } from '@app/core/services/data-task.service';
import { AgCartesianAxisOptions, AgCartesianChartOptions, AgCartesianSeriesOptions, AgPolarAxisOptions, AgPolarChartOptions, AgPolarSeriesOptions } from 'ag-charts-community';

@Injectable({
    providedIn: 'root',
})
export class DashboardService {
    private webpageDashboardWidgetKeynos?: { [key: string]: KpiTypes[] };
    private webpageDashboardWidgetRequest?: Promise<{ [key: string]: KpiTypes[] }>;

    constructor(private datatask: DataTaskService) {}

    /**
     * Persists the position of the given kpi compo.
     *
     * @param params the kpi compo data to use for persisiting the position.
     * @returns a promise resolving when the weidgets position has been persisted.
     */
    public saveWidgetPosition(params: DashboardWidgetPosition): Promise<{ errorcode: string; errormessage: string }[]> {
        return this.datatask.Post(3524, params);
    }

    /**
     * Sets the position of the widget by the given webpage name.
     *
     * @param webpageName the webpage name of the dashboard to set the position for.
     * @param params the position to persist.
     */
    public setWidgetPosition(webpageName: string, params: Omit<Omit<KpiCompBaseData, 'kpislicers'>, 'tile_type'>) {
        const index = this.webpageDashboardWidgetKeynos?.[webpageName]?.findIndex((widg) => widg.kpicomp_keyno === params.kpicomp_keyno);

        if (index !== undefined && index !== -1) {
            this.webpageDashboardWidgetKeynos![webpageName][index] = { ...this.webpageDashboardWidgetKeynos![webpageName][index], ...params };
        }
    }

    public async getDashboardWidgetKeynos(webpageName: string): Promise<KpiTypes[]> {
        return this.getWebpageDashboardWidgetKeynos(webpageName);
    }

    /**
     * Returns list of widgets that should be displayed for the webpage of the given webpagename. If webpagename not provided, returns
     * the whole map of webpagenames -> kpi types.
     *
     * @param webpageName the name of the webpage to get widgets for, if not provided returns a map for them all.
     * @returns list of widgets or a map of list of widgets.
     */
    public async getWebpageDashboardWidgetKeynos(webpageName: string): Promise<KpiTypes[]>;
    public async getWebpageDashboardWidgetKeynos(): Promise<{ [key: string]: KpiTypes[] }>;
    public async getWebpageDashboardWidgetKeynos(webpageName?: string): Promise<KpiTypes[] | { [key: string]: KpiTypes[] }> {
        if (!!webpageName) {
            const widgetsByWebpage = await this.getAllWebpageDashboardWidgetKeynos();
            return widgetsByWebpage[webpageName] ?? [];
        } else {
            return this.getAllWebpageDashboardWidgetKeynos();
        }
    }

    public getDashboardContextKey(context: DashboardContext): string {
        const normalizedContext = this.normalizeContext(context);

        return `${this.sanitizeContextValue(normalizedContext.argtype)}.${this.sanitizeContextValue(normalizedContext.argvalue)}`;
    }

    private async getAllWebpageDashboardWidgetKeynos(): Promise<{ [key: string]: KpiTypes[] }> {
        if (this.webpageDashboardWidgetKeynos) {
            return this.webpageDashboardWidgetKeynos;
        }

        if (this.webpageDashboardWidgetRequest) {
            return this.webpageDashboardWidgetRequest;
        }

        this.webpageDashboardWidgetRequest = this.datatask
            .Post(3517, { layout_columns: this.getCurrentColumnLayout() }, { returnRawResponse: true })
            .then((response: KpiDataResponse) => {
                const widgetsByWebpage: { [key: string]: KpiTypes[] } = {};

                const kpiKeynos = response.data ?? [];
                for (const kpi of kpiKeynos) {
                    const webpages = Array.isArray(kpi.webpage_name) ? kpi.webpage_name : [kpi.webpage_name];

                    for (const webpage of new Set(webpages.filter((webpageName): webpageName is string => !!webpageName))) {
                        widgetsByWebpage[webpage] = [...(widgetsByWebpage[webpage] ?? []), ...this.mapKpiResponse({ ...response, data: [kpi] })];
                    }
                }

                this.webpageDashboardWidgetKeynos = widgetsByWebpage;

                return widgetsByWebpage;
            })
            .finally(() => {
                this.webpageDashboardWidgetRequest = undefined;
            });

        return this.webpageDashboardWidgetRequest;
    }

    private mapKpiResponse(response: KpiDataResponse): KpiTypes[] {
        const kpiKeynos = response.data ?? [];
        const kpiSlicers = response.kpislicer ?? [];

        return kpiKeynos.map((kpi) => this.getKpiType(kpi, kpiSlicers));
    }

    /**
     * Returns the appropriate kpi type for the given kpi component base data.
     *
     * @param kpi the kpi component base data to return kpi type for.
     * @param kpiSlicers the available splicers that can be added on the kpi types.
     * @returns list of kpi types that will be turned into tiles in the dashboard.
     */
    private getKpiType(kpi: KpiResponseData, kpiSlicers: KpiSlicerBaseData[]) {
        if (kpi.tile_type === 'graph') {
            return <KpiTypes>{ kpicomp_keyno: kpi.kpicomp_keyno, h: kpi.h, w: kpi.w, x: kpi.x, y: kpi.y, kpislicers: kpi.kpislicers, tile_type: 'graph' };
        } else {
            const type: any = { ...kpi };
            delete type.kpislicers;
            delete type.webpage_name;
            delete type.argtype;
            delete type.argvalue;

            const kpislicer = kpiSlicers.find((slicer) => +slicer.kpislicer_keyno + 100000 === +kpi.kpicomp_keyno);
            return <KpiTypes>{ ...type, kpicomp_keyno: kpi.kpicomp_keyno, h: kpi.h, w: kpi.w, x: kpi.x, y: kpi.y, tile_type: 'slicer', ...kpislicer };
        }
    }

    private normalizeContext(context: DashboardContext): DashboardContext {
        if (!context.argtype || context.argvalue === undefined || context.argvalue === null) {
            throw Error('Dashboard context requires argtype and argvalue.');
        }

        return { argtype: '' + context.argtype, argvalue: '' + context.argvalue };
    }

    private sanitizeContextValue(value: string | number): string {
        return ('' + value).replace(/[^a-zA-Z0-9_-]/g, '_');
    }

    /**
     * Retrieves the current layout settings based on the window size.
     *
     * @returns the number of columns currently used based on the window setting.
     */
    private getCurrentColumnLayout(): 12 | 6 | 3 {
        let columns: 12 | 6 | 3 = 12;

        if (document.body.clientWidth <= 700) {
            columns = 3;
        } else if (document.body.clientWidth <= 1100) {
            columns = 6;
        }

        return columns;
    }

    public mapCartesianData(type: 'line' | 'bar', chartData: { xkey: string; ykey: string; yname: string; yvalue: number }[]): Partial<AgCartesianChartOptions> {
        const unqiueSeries = new Set(chartData.map((data) => data.ykey));
        let series: AgCartesianSeriesOptions[] = [];
        let datas: any[] = [];

        for (let key of unqiueSeries) {
            let keyName = chartData.find((data) => data.ykey === key)?.yname;
            series.push(<AgCartesianSeriesOptions>{
                type: type,
                xKey: 'xkey',
                yKey: key,
                yName: keyName ?? key,
                tooltip: {
                    renderer: (params: any) => {
                        return {
                            label: params.yName,
                            content: params.datum['xkey'] + ': ' + (!isNaN(+params.datum['yvalue']) ? Intl.NumberFormat(navigator.language).format(+params.datum['yvalue']) : params.datum['yvalue']),
                        };
                    },
                },
            });

            for (let data of chartData.filter((data) => data.ykey == key)) {
                datas.push({ ...data, [key]: data.yvalue });
            }
        }

        const axes: AgCartesianAxisOptions[] = [
            {
                type: 'category',
                position: 'bottom',
                label: { autoRotate: false },
            },
            {
                type: 'number',
                position: 'left',
                label: {
                    autoRotate: false,
                    formatter(params) {
                        const length = Math.floor(document.body.clientWidth / 100);
                        return !isNaN(+params.value) ? Intl.NumberFormat(navigator.language).format(+params.value) : params.value.length > length ? params.value.substring(0, length) + '...' : params.value;
                    },
                },
            },
        ];

        return { axes: axes, series: series, data: datas };
    }

    public mapHorizontalCartesianData(chartData: { xkey: string; ykey: string; yname: string; yvalue: number }[]): Partial<AgCartesianChartOptions> {
        const uniqueSeries = new Set(chartData.map((data) => data.xkey));
        let series: AgCartesianSeriesOptions[] = [];
        let datas: any[] = [];

        for (let key of uniqueSeries) {
            series.push({
                type: 'bar',
                direction: 'horizontal',
                xKey: 'yname',
                xName: 'yname',
                yKey: key,
                yName: key,
                tooltip: {
                    renderer: (params: any) => {
                        return {
                            label: params.yName,
                            content: params.datum.yname + ': ' + (!isNaN(+params.datum['yvalue']) ? Intl.NumberFormat(navigator.language).format(+params.datum['yvalue']) : params.datum['yvalue']),
                        };
                    },
                },
            });

            for (let data of chartData.filter((data) => data.xkey == key)) {
                datas.push({ ...data, [key]: data.yvalue });
            }
        }

        const axes: AgCartesianAxisOptions[] = [
            {
                type: 'category',
                position: 'left',
                label: {
                    autoRotate: false,
                    formatter(params) {
                        const length = Math.floor(document.body.clientWidth / 100);
                        return params.value.length > length ? params.value.substring(0, length) + '...' : params.value;
                    },
                },
            },
            {
                type: 'number',
                position: 'bottom',
                label: {
                    autoRotate: false,
                    formatter(params) {
                        const length = Math.floor(document.body.clientWidth / 100);
                        return !isNaN(+params.value) ? Intl.NumberFormat('no').format(+params.value) : params.value.length > length ? params.value.substring(0, length) + '...' : params.value;
                    },
                },
            },
        ];

        return { axes: axes, series: series, data: datas };
    }

    public mapPolarData(type: 'pie' | 'donut', chartData: { xkey: string; ykey: string; yname: string; yvalue: number }[]): Partial<AgPolarChartOptions> {
        let series: AgPolarSeriesOptions[] = [{ type: type, angleKey: 'yvalue', calloutLabelKey: 'yname', innerRadiusRatio: 0.7 }];
        let datas: any[] = chartData;
        let axes: AgPolarAxisOptions[] = [
            {
                type: 'angle-category',
                label: {
                    formatter(params) {
                        const length = Math.floor(document.body.clientWidth / 100);
                        return params.value.length > length ? params.value.substring(0, length) + '...' : params.value;
                    },
                },
            },
        ];

        return { series: series, data: datas, axes: axes };
    }

    public getChartType(data: string) {
        if (data === 'GRAPH_LINE') {
            return 'line';
        } else if (data === 'PIE') {
            return 'pie';
        } else if (data === 'DONUT') {
            return 'donut';
        } else {
            return 'bar';
        }
    }
}

/**
 * Represents the data response when requesting kpi tiles.
 */
interface KpiDataResponse {
    /**
     * The error message if an error occured on the request.
     */
    message: string;

    /**
     * Whether an error occurred on the request.
     */
    iserror: boolean;

    /**
     *
     */
    data: KpiResponseData[];

    /**
     * List over possible kpi splicer configurations.
     */
    kpislicer: KpiSlicerBaseData[];
}

export interface DashboardContext {
    argtype: string;
    argvalue: string | number;
}

export interface DashboardWidgetPosition {
    kpicomp_keyno: number;
    webpage_name: string;
    h: number;
    w: number;
    x: number;
    y: number;
    layout_columns?: number;
}

type KpiResponseData = KpiCompBaseData & { webpage_name?: string | string[]; argtype?: string; argvalue?: string | number };

/**
 * Represents data for a kpi slicer.
 */
export interface KpiSlicerBaseData {
    /**
     * The id of the kpi splicer.
     */
    kpislicer_keyno: string | number;

    kpicomp_keyno: string | number;

    /**
     * The datatask to use for loading options into the slicer.
     */
    p2_datatask_keyno: string | number;

    /**
     * The parameter name used to identify the slice choices sent into the kpi get data function.
     */
    parameter_name: string;

    /**
     * The key name for the property which displays the name for the options in the list.
     */
    data_name: string;

    /**
     * The key name for the property which holds the id for the options in the list.
     */
    data_id: string;
}

/**
 * Represents data for connecting kpi-comp to kpi slicer.
 */
export interface KpiCompKpiSlicerData {
    /**
     * The kpi slicer id.
     */
    kpislicer_keyno: string | number;
}

/**
 * Represents basic data for a kpi component.
 */
export interface KpiCompBaseData<TType extends string = 'graph'> {
    /**
     * The id of the kpi component.
     */
    kpicomp_keyno: string;

    /**
     * The number of cells to span for the components height.
     */
    h: string;

    /**
     * The number of cells to span for the components width.
     */
    w: string;

    /**
     * The x coordinate for the upper left corner.
     */
    x: string;

    /**
     * The y coordinate for the upper left corner.
     */
    y: string;

    /**
     * List of kpi slicers this kpi component is using.
     */
    kpislicers: KpiCompKpiSlicerData[];

    /**
     * The tile type for kpi component.s
     */
    tile_type: TType;
}

/**
 * Represents a kpi tile for splicing data.
 */
export type KpiSlicerData = KpiCompBaseData<'slicer'> & KpiSlicerBaseData;

/**
 * The different types of kpi tiles to display in a dashboard.
 */
export type KpiTypes = KpiCompBaseData | KpiSlicerData;
